import {
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import StdFormDialog from "components/common/form/StdFormDialog";
import StdImageFile from "components/common/form/StdImageFile";
import { useSearchMaster, useSearchOptions } from "models/SearchMasterModel";
import { TUser, useUser } from "models/UserModel";
import ErrorBoundaryProvider from "providers/ErrorBoundaryProvider";
import { Suspense, useCallback, useState } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";

export const openAccountEditFormAtom = atom<boolean>({
  key: "openAccountEditForm",
  default: false,
});

export const accountEditFormFocusAtom = atom<string | null>({
  key: "accountEditFormFocusAtom",
  default: null,
});

const AccountEditFormDialog = () => {
  const { getUser, patchUser, uploadUserAvatar, refetchUser } = useUser();
  const user = getUser();
  const [open, setOpen] = useRecoilState(openAccountEditFormAtom);
  const [focus, setForcus] = useRecoilState(accountEditFormFocusAtom);

  const handleOnClose = useCallback(() => {
    setForcus(null);
    setOpen(false);
  }, [setForcus, setOpen]);

  const handleOnSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const newData = {
        first_name: data.get("first_name"),
        last_name: data.get("last_name"),
        shiten_cd: data.get("SHITEN_CD") === "_none" ? null : data.get("SHITEN_CD"),
        avatar_file_id: data.get("avatar_file_id"),
      };
      await patchUser(newData);
      refetchUser();
      handleOnClose();
    },
    [handleOnClose, patchUser, refetchUser],
  );

  return (
    <StdFormDialog
      title="アカウント編集"
      openState={open}
      handleOnSubmit={handleOnSubmit}
      handleOnClose={handleOnClose}
    >
      <FormControl>
        <Box sx={{ display: "flex" }}>
          <TextField
            margin="normal"
            required
            id="last_name"
            label="氏"
            name="last_name"
            autoComplete="last_name"
            autoFocus={!focus}
            defaultValue={user?.lastName}
            size="small"
            sx={{ mr: 2 }}
          />
          <TextField
            margin="normal"
            required
            id="first_name"
            label="名"
            name="first_name"
            autoComplete="first_name"
            defaultValue={user?.firstName}
            size="small"
          />
        </Box>
        <ErrorBoundaryProvider>
          <Suspense fallback={<LinearProgress />}>
            <ShitenSelect user={user} />
          </Suspense>
        </ErrorBoundaryProvider>
      </FormControl>
      <Box>
        <StdImageFile
          name="avatar"
          title="Avatar Image"
          defaultUrl={user?.avatar_url}
          defaultFileId={user?.avatar_file_id}
          uploader={uploadUserAvatar}
        />
      </Box>
    </StdFormDialog>
  );
};

const ShitenSelect = ({ user }: { user: TUser }) => {
  const { getSearchMasterByProvideKey } = useSearchMaster();
  const ShitenSearchMaster = getSearchMasterByProvideKey("SHITEN_CD");
  const { data: ShitenData } = useSearchOptions(ShitenSearchMaster);
  const ShitenOptions = [{ SHITEN_CD: "_none", NAME: "- 未設定 -" }, ...ShitenData];
  const [shitenCD, setShitenCD] = useState(user?.shitenCD ? user.shitenCD : "_none");
  const handleOnChangeShiten = useCallback((event: SelectChangeEvent) => setShitenCD(event.target.value), []);
  const focus = useRecoilValue(accountEditFormFocusAtom);
  return (
    <FormControl fullWidth size="small" sx={{ my: 2, width: "150px" }}>
      <InputLabel id="shiten-label">支店</InputLabel>
      <Select
        name="SHITEN_CD"
        label="支店"
        labelId="shiten-label"
        value={shitenCD}
        defaultValue={undefined}
        onChange={handleOnChangeShiten}
        autoFocus={focus === "shiten"}
      >
        {(ShitenOptions as { SHITEN_CD: any; NAME: string }[]).map((op, index) => {
          return (
            <MenuItem key={index} value={op.SHITEN_CD} dense={true}>
              {op.NAME}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default AccountEditFormDialog;
